import { Button, Form } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useNotification } from "~hooks/useNotification";
import Page from "../../../Common/Page";
import {
  FixtureAdvancedSection,
  FixtureBasicSection,
  FixtureCompetitorsSection,
  FixtureEntrantsSkeleton,
  FixtureParticipantsSection,
} from "./components";
import { formStyles, StyledFormGroupsContainer } from "./FixtureEditPage.styles";
import { useEditFixtureFormSubmit, useEditFixturePageTitle, useGetFixture } from "./hooks";
import { StyledEntrantSection } from "./styles";
import type { EditFixtureForm } from "./types";

const FixtureEditPage = () => {
  const { formatMessage } = useIntl();
  const { data: fixtureData, status: fixtureQueryStatus, isLoading, isFetching } = useGetFixture();
  const [isUpdating, setIsUpdating] = useState(false);
  const notification = useNotification();

  const [form] = Form.useForm<EditFixtureForm>();

  // form sync with BE
  useEffect(() => {
    if (fixtureQueryStatus === "success" && fixtureData) {
      form.setFieldsValue(fixtureData.formData);
    }
  }, [fixtureData, fixtureQueryStatus, form]);

  // form submission
  const onFinish = useEditFixtureFormSubmit((results) => {
    setIsUpdating(false);

    const allFailed = results.every((result) => result.status === "rejected");
    const allSuccessful = results.every((result) => result.status === "fulfilled");
    const placement = "bottomRight";

    // not handling error messages as it handled on top level in `useApi`
    if (allFailed) return;

    if (!allSuccessful) {
      return notification.warning({
        message: formatMessage({
          id: "form.submission.partialSuccess",
        }),
        placement,
      });
    }

    notification.success({
      message: formatMessage({
        id: "fixture.submission.success",
      }),
      placement,
    });
  });

  const pageTitles = useEditFixturePageTitle();
  const { hasPermission } = useUserPermissions();

  const isCompetitors = fixtureData?.competitorType === CompetitorTypes.Entity;
  const isParticipants = fixtureData?.competitorType === CompetitorTypes.Person;

  const readFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.READ);
  const readParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.READ);
  const readCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.READ);
  const nothingAllowed = !readFixtureAllowed && !readParticipantsAllowed && !readCompetitorsAllowed;

  const editFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const editParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.UPDATE);
  const editCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.UPDATE);
  const isSubmitAllowed = editFixtureAllowed && (isParticipants ? editParticipantsAllowed : editCompetitorsAllowed);

  const navigate = useNavigate();
  const { customerId, sport, organizationId } = useParams();
  useEffect(() => {
    if (nothingAllowed) navigate(`/${customerId}/admin/${sport}/${organizationId}/403`);
  }, [nothingAllowed, customerId, sport, organizationId, navigate]);

  return (
    <Page {...pageTitles}>
      <Form<EditFixtureForm>
        form={form}
        onFinish={(values) => {
          setIsUpdating(true);
          onFinish(values);
        }}
        style={formStyles}
      >
        <StyledFormGroupsContainer>
          <FixtureBasicSection isUpdating={isUpdating} />
          {readFixtureAllowed && <FixtureAdvancedSection isUpdating={isUpdating} />}

          {isLoading && <FixtureEntrantsSkeleton />}
          <StyledEntrantSection>
            {isCompetitors && readCompetitorsAllowed && (
              <>
                <FixtureCompetitorsSection isUpdating={isUpdating} isHome />
                <FixtureCompetitorsSection isUpdating={isUpdating} />
              </>
            )}
            {isParticipants && readParticipantsAllowed && (
              <>
                <FixtureParticipantsSection isUpdating={isUpdating} isHome />
                <FixtureParticipantsSection isUpdating={isUpdating} />
              </>
            )}
          </StyledEntrantSection>
        </StyledFormGroupsContainer>
        {isSubmitAllowed && (
          <Button htmlType="submit" type="primary" disabled={isUpdating} loading={(isFetching || isUpdating) && !isLoading}>
            {formatMessage({ id: "fixture.update.button" })}
          </Button>
        )}
      </Form>
    </Page>
  );
};

export default FixtureEditPage;
